'use client';

import type { Category } from '@eliadmin/core/src/api/slugs/types/slug-detail-types';

import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchProducts } from '@eliadmin/core/src/api/products/product-api';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { CategoryParamsSync, useCategoryStore } from '@eliadmin/core/src/stores/category-store';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { buildProductsQuery } from '@eliadmin/core/src/utils/product-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useSearchParams } from 'next/navigation';
import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Paragraph } from '../ui/paragraph';
import CategorySort from '@eliadmin/core/src/components/categories/category-sort';
import { CategoryFilterSheet } from '@eliadmin/core/src/components/categories/category-filter-sheet';
import { AttributeType } from '@eliadmin/core/src/stores/category-store';
import { ProductCard, ProductCardSkeleton } from '../product/product-card';
import { CategoryPagination } from './category-pagination';
import { Button } from '../ui/button';
import { CategoryFilterSelect } from './category-filter-select';
import {
  CATEGORY_PAGE_SIZE,
  getCategoryFirstPageSeed,
  type CategoryFirstPage,
} from '@/utils/category-first-page';
import { getCategoryPaginationFilterIdentity } from '@/utils/category-pagination';
import {
  canLoadMoreCategoryProducts,
  getCategoryPlaceholder,
  getVisibleCategoryData,
  type CategoryPlaceholderScope,
} from '@/utils/category-placeholder';
import { CatalogueNavigationReady } from '@/components/catalogue-navigation';
import { CATEGORY_FILTER_PAGE_EVENT } from '@/utils/category-filter-actions';

const CATEGORY_GRID_CLASS_NAME =
  'grid w-full grid-cols-2 gap-x-2 gap-y-4 sm:gap-x-3 sm:gap-y-6 md:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5';
const CATEGORY_GRID_SECTION_CLASS_NAME =
  'gap-6 pb-8 pt-8 sm:gap-8 sm:pb-10 sm:pt-10 md:gap-10 lg:gap-12 lg:pb-12 lg:pt-12';

export type CategoryProps = {
  className?: {
    grid_wrapper?: string;
    product?: string;
  };
  category: Category;
  onProductTotalChange?: (total: number) => void;
  paginationProps?: { scrollToElementAfterPageChange?: string };
  locale: string;
  initialPage?: CategoryFirstPage;
};

// Keep request-only URL access below its own boundary, so the product grid can
// be included in ISR HTML rather than replacing the entire grid with a skeleton.
function CategoryQueryObserver({ onChange }: { onChange: (search: string | undefined) => void }) {
  const search = useSearchParams().toString();
  const setFilter = useCategoryStore(state => state.setFilter);
  const setManufacturers = useCategoryStore(state => state.setManufacturers);
  const [historyRevision, setHistoryRevision] = useState(0);
  const [pendingHistorySearch, setPendingHistorySearch] = useState<string | null>(null);
  const pendingFilterPage = useRef(false);
  useEffect(() => {
    const onFilterPage = (event: Event) => {
      pendingFilterPage.current = true;
      onChange((event as CustomEvent<string>).detail);
    };
    window.addEventListener(CATEGORY_FILTER_PAGE_EVENT, onFilterPage);
    return () => window.removeEventListener(CATEGORY_FILTER_PAGE_EVENT, onFilterPage);
  }, [onChange]);
  useEffect(() => {
    const pathname = window.location.pathname;
    const restoreFromHistory = () => {
      pendingFilterPage.current = false;
      if (window.location.pathname !== pathname) return;
      const targetSearch = new URLSearchParams(window.location.search).toString();
      if (
        getCategoryPaginationFilterIdentity(search) ===
        getCategoryPaginationFilterIdentity(targetSearch)
      ) {
        onChange(targetSearch);
        return;
      }
      // Keep the shared parser as the sole authority. Its fresh mount must see
      // the new Next URL context, which may arrive after the native popstate.
      onChange(undefined);
      setPendingHistorySearch(targetSearch);
      setHistoryRevision(revision => revision + 1);
      setManufacturers([]);
      setFilter({
        availabilities: [],
        attributes: [],
        sort: { order: null, orderBy: null },
        price_min: 0,
        price_max: 0,
        discounted: false,
      });
    };
    window.addEventListener('popstate', restoreFromHistory);
    return () => window.removeEventListener('popstate', restoreFromHistory);
  }, [onChange, search, setFilter, setManufacturers]);
  useEffect(() => {
    if (pendingHistorySearch !== null && pendingHistorySearch !== search) return;
    if (pendingFilterPage.current) {
      const query = new URLSearchParams(search);
      if (query.get('page') !== '1') {
        // Core URL serialization may still observe the pre-action URL snapshot.
        // Never publish that stale page to the grid while the filter action settles.
        query.set('page', '1');
        window.history.replaceState(
          null,
          '',
          `${window.location.pathname}?${query}${window.location.hash}`,
        );
        return;
      }
      pendingFilterPage.current = false;
    }
    onChange(search);
    if (pendingHistorySearch !== null) setPendingHistorySearch(null);
  }, [onChange, search, historyRevision, pendingHistorySearch]);
  // The child's mount effect applies URL filters before the parent publishes
  // readiness. Keep both in this boundary, with a single shared-core parser.
  return pendingHistorySearch !== null && pendingHistorySearch !== search ? null : (
    <CategoryParamsSync key={historyRevision} />
  );
}

export function CategoryContent(props: CategoryProps) {
  const { me, isInitialized } = useCustomerStore();
  const authToken = me?.authToken;
  const filters = useCategoryStore(s => s.filter);
  const category = useCategoryStore(s => s.category);

  const selectAttributes = useMemo(
    () =>
      (category.attributes ?? [])
        .filter(a => a.type === AttributeType.SELECT && a.name && a.values?.length)
        .slice(0, 3),
    [category.attributes],
  );
  const [search, setSearch] = useState<string>();
  const hasObservedSearch = useRef(false);
  useEffect(() => {
    if (search !== undefined) hasObservedSearch.current = true;
  }, [search]);
  const currentPage = new URLSearchParams(search).get('page');
  const queryObserver = (
    <Suspense fallback={null}>
      <CategoryQueryObserver onChange={setSearch} />
    </Suspense>
  );

  const { t } = useTranslations();

  const attributesKey = JSON.stringify(filters.attributes ?? []);

  const queryWithoutPage = useMemo(
    () =>
      buildProductsQuery({
        attributes: filters.attributes,
        categories: [props.category?.id],
        manufacturers: filters.manufacturers ?? [],
        results: CATEGORY_PAGE_SIZE,
        order: filters.sort.order,
        orderBy: filters.sort.orderBy,
        // values: filters.attributes.flatMap(attr => attr.values),
        price_from: filters.price_min,
        price_to: filters.price_max,
        availabilities: filters.availabilities,
        suppliers: filters.suppliers,
      }),
    [
      props.category?.id,
      filters.manufacturers,
      filters.sort.order,
      filters.sort.orderBy,
      filters.price_min,
      filters.price_max,
      filters.availabilities,
      filters.suppliers,
      filters.attributes,
    ],
  );
  const queryWithoutPageKey = JSON.stringify(queryWithoutPage);
  const initialPage = getCategoryFirstPageSeed(props.initialPage, {
    categoryId: props.category.id,
    locale: props.locale,
    isAuthenticated: Boolean(me),
    query: queryWithoutPage,
    hasAttributes: Boolean(filters.attributes?.length),
    search,
  });
  const placeholderScope = useMemo<CategoryPlaceholderScope>(
    () => ({
      initialized: isInitialized && search !== undefined,
      categoryId: props.category.id,
      locale: props.locale,
      customerId: me?.customer.id ?? null,
      authToken: authToken ?? null,
      queryIdentity: queryWithoutPageKey,
      attributesIdentity: attributesKey,
      urlFilterIdentity: getCategoryPaginationFilterIdentity(search ?? ''),
    }),
    [
      isInitialized,
      search,
      props.category.id,
      props.locale,
      me?.customer.id,
      authToken,
      queryWithoutPageKey,
      attributesKey,
    ],
  );

  const productsQuery = useInfiniteQuery({
    queryKey: [
      CacheKey.PRODUCTS,
      'infinite',
      currentPage,
      queryWithoutPageKey,
      authToken,
      me?.customer.id,
      props.locale,
      attributesKey,
    ],
    queryFn: ({ pageParam }) => {
      return fetchProducts({
        query: {
          ...queryWithoutPage,
          page: pageParam,
        },
        locale: props.locale,
        authToken,
      });
    },
    getNextPageParam: lastPage => {
      if (lastPage?.meta?.current_page < lastPage?.meta?.last_page)
        return lastPage?.meta?.current_page + 1;
      return undefined;
    },
    initialPageParam: currentPage ? parseInt(currentPage) : 1,
    initialData: initialPage ? { pages: [initialPage.page], pageParams: [1] } : undefined,
    initialDataUpdatedAt: initialPage?.fetchedAt,
    meta: { categoryPlaceholderScope: placeholderScope },
    placeholderData: (previousData, previousQuery) =>
      getCategoryPlaceholder(
        previousData,
        previousQuery?.meta?.categoryPlaceholderScope,
        placeholderScope,
      ),
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
    enabled: isInitialized && search !== undefined,
  });

  const canFetchProducts = isInitialized && search !== undefined;
  // Keep a failed page transition recoverable without discarding the old geometry.
  // The same scope guard also prevents a private result surviving logout/login.
  const lastSuccessful = useRef<{
    scope: CategoryPlaceholderScope;
    data: NonNullable<typeof productsQuery.data>;
  } | null>(null);
  useEffect(() => {
    if (!productsQuery.data || productsQuery.isPlaceholderData || productsQuery.isError) return;
    lastSuccessful.current = { scope: placeholderScope, data: productsQuery.data };
  }, [
    productsQuery.data,
    productsQuery.isPlaceholderData,
    productsQuery.isError,
    placeholderScope,
  ]);
  const errorPlaceholder =
    productsQuery.isError || productsQuery.isPending
      ? getCategoryPlaceholder(
          lastSuccessful.current?.data,
          lastSuccessful.current?.scope,
          placeholderScope,
        )
      : undefined;
  const publicSeed =
    initialPage && (search !== undefined || !hasObservedSearch.current)
      ? { pages: [initialPage.page], pageParams: [1] }
      : undefined;
  const displayedData = getVisibleCategoryData({
    ready: canFetchProducts,
    queryData: productsQuery.data,
    errorPlaceholder,
    publicSeed,
  });
  const lastPage = displayedData?.pages[displayedData.pages.length - 1];
  const flatList = displayedData?.pages.flatMap(page => page.data) ?? [];
  const showingPreviousPage = Boolean(productsQuery.isPlaceholderData || errorPlaceholder);
  const showSkeleton = !displayedData && (!canFetchProducts || productsQuery.isPending);
  const isBusy = showSkeleton || productsQuery.isFetching;
  const canLoadMore = canLoadMoreCategoryProducts({
    ready: canFetchProducts,
    pending: productsQuery.isPending,
    placeholder: showingPreviousPage,
    error: productsQuery.isError,
    fetching: productsQuery.isFetching,
  });
  const productScope = JSON.stringify([
    me?.customer.id ?? 'guest',
    props.locale,
    queryWithoutPageKey,
    attributesKey,
  ]);
  const [loadMode, setLoadMode] = useState<'manual' | 'auto'>('manual');
  const [fetchTrigger, setFetchTrigger] = useState<'load-more' | 'show-all' | 'auto' | null>(null);
  const autoLoadRef = useRef<HTMLDivElement | null>(null);

  const fetchNextPageSafely = useCallback(
    async (trigger: 'load-more' | 'show-all' | 'auto') => {
      // Imperative fetchNextPage bypasses the query's enabled flag.
      if (!canLoadMore) return false;
      if (!productsQuery.hasNextPage) return false;
      if (productsQuery.isFetchingNextPage) return false;
      setFetchTrigger(trigger);
      try {
        await productsQuery.fetchNextPage();
        return true;
      } finally {
        setFetchTrigger(null);
      }
    },
    [canLoadMore, productsQuery],
  );

  useEffect(() => {
    if (loadMode !== 'auto') return;
    if (!productsQuery.hasNextPage) return;

    const target = autoLoadRef.current;
    if (!target) return;

    const observer = new IntersectionObserver(
      entries => {
        const entry = entries[0];
        if (!entry?.isIntersecting) return;
        void fetchNextPageSafely('auto');
      },
      { root: null, rootMargin: '250px 0px', threshold: 0 },
    );

    observer.observe(target);

    return () => {
      observer.disconnect();
    };
  }, [fetchNextPageSafely, loadMode, productsQuery.hasNextPage]);

  useEffect(() => {
    setLoadMode('manual');
    setFetchTrigger(null);
  }, [currentPage, props.locale, authToken, queryWithoutPageKey, attributesKey]);

  useEffect(() => {
    if (lastPage?.meta?.total === undefined) return;
    props.onProductTotalChange?.(lastPage.meta.total);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastPage?.meta?.total]);

  const hasProducts = flatList.length > 0;

  return (
    <>
      {queryObserver}
      {displayedData && !showingPreviousPage && !showSkeleton && (
        <CatalogueNavigationReady slug={props.category.slug} />
      )}
      <section
        id="category-content"
        className={cn(
          'custom-section',
          'z-[35] flex flex-col items-center justify-center gap-6 bg-background py-3',
          'ea-category-content__actions-bar',
        )}
        style={{ top: 'calc(var(--header-height, 0px) + 0px)' }}
      >
        <div className={cn('custom-container', 'hidden items-center justify-between md:flex')}>
          <div className="flex items-center justify-start gap-3">
            <CategoryFilterSheet />
            <div className="hidden items-center gap-2 md:flex">
              {selectAttributes.map(attr => (
                <CategoryFilterSelect key={attr.id} attribute={attr} />
              ))}
            </div>
          </div>
          <div className="flex items-center justify-end gap-6 xl:gap-12">
            <Paragraph color="muted-foreground" className="whitespace-nowrap">
              {displayedData?.pages[0]?.meta?.total ?? '–'} {t('text3744')}
            </Paragraph>
            <CategorySort />
          </div>
        </div>
        <div className="flex w-full md:hidden">
          <CategoryFilterSheet />
        </div>
      </section>
      <div className={cn('custom-section', 'flex md:hidden')}>
        <CategorySort />
        <Paragraph color="muted-foreground" className="mt-6 whitespace-nowrap">
          {displayedData?.pages[0]?.meta?.total ?? '–'} {t('text3744')}
        </Paragraph>
      </div>
      <section className={cn('custom-section', CATEGORY_GRID_SECTION_CLASS_NAME)}>
        <div className={cn('custom-container', 'flex items-start justify-between gap-4 lg:gap-6')}>
          <div
            className={cn(CATEGORY_GRID_CLASS_NAME, showingPreviousPage && 'opacity-50')}
            aria-busy={isBusy}
            inert={showingPreviousPage || !canFetchProducts}
          >
            {showSkeleton ? (
              <ProductCardSkeleton amount={CATEGORY_PAGE_SIZE} variant="vertical" />
            ) : hasProducts ? (
              flatList.map(product => (
                <ProductCard product={product} key={`${productScope}:${product.slug}`} />
              ))
            ) : !productsQuery.isError ? (
              <Paragraph
                size="lg"
                color="muted-foreground"
                className="col-span-full mb-4 mt-12 text-center"
              >
                {t('text3687')}
              </Paragraph>
            ) : null}
          </div>
        </div>
        {!!productsQuery.isError && (
          <div role="alert" className="flex flex-col items-center gap-3">
            <Paragraph>{t('text530')}</Paragraph>
            <Button
              variant="secondary"
              disabled={!canFetchProducts || productsQuery.isFetching}
              onClick={() => void productsQuery.refetch()}
            >
              {t('text595')}
            </Button>
          </div>
        )}
        {loadMode === 'manual' && !!productsQuery.hasNextPage && (
          <div
            className={cn(
              'flex flex-col justify-center gap-2 md:flex-row',
              'ea-category-products__load-more',
            )}
          >
            <Button
              variant="secondary"
              disabled={!canLoadMore}
              isLoading={productsQuery.isFetchingNextPage && fetchTrigger === 'load-more'}
              onClick={() => void fetchNextPageSafely('load-more')}
            >
              {t('text338')}
            </Button>
          </div>
        )}

        {loadMode === 'auto' && !!productsQuery.hasNextPage && (
          <div
            className={cn(
              'flex flex-col items-center justify-center gap-2',
              'ea-category-products__auto-load',
            )}
          >
            <div ref={autoLoadRef} className="h-1 w-full" />
            {!!productsQuery.isFetchingNextPage && (
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-muted border-t-foreground" />
            )}
          </div>
        )}
        <div
          className={cn('flex min-h-9 flex-row justify-center', 'ea-category-products__pagination')}
        >
          <Suspense fallback={null}>
            <CategoryPagination
              links={lastPage?.meta.links}
              search={search}
              onPageChange={setSearch}
              disabled={!canFetchProducts}
              scrollToElementAfterPageChange={
                props.paginationProps?.scrollToElementAfterPageChange || undefined
              }
            />
          </Suspense>
        </div>
      </section>
    </>
  );
}

export function CategoryContentSkeleton() {
  return (
    <>
      <section
        id="category-content"
        aria-busy="true"
        className="custom-section z-[35] flex flex-col items-center justify-center gap-6 bg-background py-3"
      >
        <div className="custom-container hidden items-center justify-between md:flex">
          <div className="h-12 w-[160px] animate-pulse rounded-[20px] bg-neutral-100" />
          <div className="flex items-center justify-end gap-6 xl:gap-12">
            <div className="h-[18px] w-[105px] animate-pulse rounded-[20px] bg-neutral-100" />
            <div className="h-12 w-[175px] animate-pulse rounded-[20px] bg-neutral-100" />
          </div>
        </div>
        <div className="flex w-full md:hidden">
          <div className="h-12 w-full animate-pulse bg-neutral-100" />
        </div>
      </section>
      <div className="custom-section flex md:hidden" aria-hidden="true">
        <div className="h-12 w-full animate-pulse bg-neutral-100" />
        <div className="mt-6 h-6 w-[40%] animate-pulse bg-neutral-100" />
      </div>
      <section className={cn('custom-section', CATEGORY_GRID_SECTION_CLASS_NAME)} aria-busy="true">
        <div className="custom-container flex items-start justify-between gap-4 lg:gap-6">
          <div className={CATEGORY_GRID_CLASS_NAME}>
            <ProductCardSkeleton amount={CATEGORY_PAGE_SIZE} variant="vertical" />
          </div>
        </div>
        <div className="flex min-h-9 justify-center" />
      </section>
    </>
  );
}
